import {Group, GroupedList, sampleSorted} from '@wandb/weave/common/util/data';
import * as _ from 'lodash';

import {Data} from '../../state/runs/types';
import {ClassLabelNode, getMediaMetadata} from '../../util/media';
import {RunWithHistoryAndMediaWandb} from '../MediaCard';
import {Axis, NonEmptyTile} from './common';
import {
  calcGroupCount,
  getMaskOptions,
  Layout,
  MediaConfigWithDefaults,
  StepMeta,
} from './Component';
import {EmptyTile, Tile} from './tiles';
import {getStepFromSliderStep} from './utils/stepUtils';

// Helper function for creating the tile layout
export function makeLayout(
  // TODO: data and runsWithKeys should not be necessary, data should be in metadata
  data: Data,
  runsWithKey: RunWithHistoryAndMediaWandb[],
  stepMeta: StepMeta,
  config: MediaConfigWithDefaults,
  allMasks: {
    [mediaKey: string]: {
      [maskKey: string]: ClassLabelNode;
    };
  },
  currentStep: number
): Layout {
  const {
    mediaKeys,
    gallerySettings,
    gridSettings,
    mode,
    maxGalleryItems,
    stepIndex: stepFromConfig,
    mediaIndex,
    columnCount,
    selection,
    maxYAxisCount,
    snapToExistingStep = true,
  } = config;
  // NOTE: In future to make these reusable remove the null checks
  // move them to the parent and add the constraints to the types
  if (mediaKeys == null || mediaKeys.length === 0) {
    return {tiles: []};
  }
  // NOTE: In future to make these reusable remove the null checks
  // move them to the parent and add the constraints to the types
  if (stepMeta.runs.length === 0) {
    return {tiles: []};
  }

  // This is some metadata we pass out of the
  // getAxis function for the UI
  // This is a little hacky we could do it cleaner
  let historySampleRate: number | undefined;

  let galleryAxisData: number[] | null = null;
  let gridAxisData: {
    x: number[];
    y: number[];
  } | null = null;

  // We want to return the full axis as well
  // So we can render the full axis and the zoomed
  // on subset
  let xAxisTicks: number[] | undefined;
  let allXAxisValues: number[] | undefined;

  const tiles: GroupedList<Tile> = [];

  if (mode === 'gallery') {
    addTilesGalleryMode();
  } else if (mode === 'grid') {
    addTilesGridMode();
  }

  return {
    tiles,
    gridAxisData,
    galleryAxisData,
    xAxisTicks,
    allXAxisValues,
    historySampleRate,
  };

  // HELPER FUNCTIONS

  function addTilesGalleryMode(): void {
    const {axis} = gallerySettings;
    galleryAxisData = getAxisData(axis, {
      maxSize: maxGalleryItems,
    });

    const axisLength = galleryAxisData.length;

    for (let i = 0; i < axisLength; i++) {
      const runIndex = axis === 'run' ? i : 0;
      const run = runsWithKey[runIndex];
      const runStepMeta = stepMeta.runs[runIndex];
      const {step: stepIndex, sliderStep: sliderStepIndex} =
        getStepFromSliderStep(runStepMeta, currentStep ?? stepFromConfig);
      const mediaStep = axis === 'step' ? galleryAxisData[i] : stepIndex;
      const tileMediaIndex =
        axis === 'index' ? (galleryAxisData[i] as number) : mediaIndex ?? 0;
      const notes = data.filteredRunsById[run.name].notes;

      const meta =
        axis === 'camera'
          ? {camera: {cameraIndex: galleryAxisData[i]}}
          : undefined;

      const tile = {
        mediaSliderKey: runStepMeta.sliderKey,
        mediaSliderKeyValue: sliderStepIndex,
        mediaStep,
        mediaIndex: tileMediaIndex,
        run: {...run, history: run.history ?? [], notes},
        runSignature: {
          entityName: run.entityName,
          projectName: run.projectName,
          runName: run.name,
        },
        meta,
      };
      addGroupOrTileForMediaKeys(tile);
    }
  }

  function addTilesGridMode(): void {
    const {xAxis, yAxis} = gridSettings;

    // Full Axis Data for preserving the non-zoom version
    xAxisTicks = getAxisData(xAxis, {
      maxSize: columnCount,
    });

    allXAxisValues = getAxisData(xAxis);

    // Actual Axis Data(Including Zoom)
    const [minX, maxX] = [
      _.get(selection?.xAxis, 0),
      _.get(selection?.xAxis, 1),
    ];
    const [minY, maxY] = [
      _.get(selection?.yAxis, 0),
      _.get(selection?.yAxis, 1),
    ];
    const groupSize = mediaKeys?.length ?? 0;

    const xAxisData = getAxisData(xAxis, {
      maxSize: columnCount / groupSize,
      min: minX,
      max: maxX,
    });
    const yAxisData = getAxisData(yAxis, {
      maxSize: maxYAxisCount,
      min: minY,
      max: maxY,
    });
    gridAxisData = {x: xAxisData, y: yAxisData};

    // Each group is composed of 1 tile for each mediaKey
    // Or multiple if it has many mask tiles
    const groupCount = calcGroupCount(config);

    for (let row = 0; row < yAxisData.length; row++) {
      const addedToRow: GroupedList<Tile> = [];
      for (let col = 0; col < columnCount / groupCount; col++) {
        // we add empty tiles to fill the row if the number of tiles < columnCount
        if (col >= xAxisData.length) {
          let totalTiles = 0;
          addedToRow.forEach(tileOrGroup => {
            const ts =
              'items' in tileOrGroup ? tileOrGroup.items : [tileOrGroup];
            ts.forEach(t => {
              const maskOptions = getMaskOptions(
                config,
                allMasks,
                t as NonEmptyTile
              );
              totalTiles += maskOptions.length;
            });
          });
          while (totalTiles < columnCount) {
            tiles.push({
              mediaKey: mediaKeys?.[0] ?? '',
              isEmpty: true,
            } as EmptyTile);
            totalTiles++;
          }
          break;
        }

        const runIndex = xAxis === 'run' ? col : yAxis === 'run' ? row : 0;
        const run = runsWithKey[runIndex];
        const runStepMeta = stepMeta.runs[runIndex];
        const stepIndex =
          xAxis === 'step'
            ? xAxisData[col]
            : yAxis === 'step'
            ? yAxisData[row]
            : stepFromConfig;
        const {step: mediaStep, sliderStep: sliderStepIndex} =
          getStepFromSliderStep(runStepMeta, stepIndex);

        const tileMediaIndex =
          xAxis === 'index'
            ? (xAxisData[col] as number)
            : yAxis === 'index'
            ? yAxisData[row]
            : mediaIndex ?? 0;

        const notes = data.filteredRunsById[run.name].notes;

        const tile = {
          mediaSliderKey: runStepMeta.sliderKey,
          mediaSliderKeyValue: sliderStepIndex,
          mediaStep,
          mediaIndex: tileMediaIndex,
          run: {...run, history: run.history ?? [], notes},
          runSignature: {
            entityName: run.entityName,
            projectName: run.projectName,
            runName: run.name,
          },
        };

        addedToRow.push(addGroupOrTileForMediaKeys(tile));
      }
    }
  }

  // Give the step, index, and run, construct a single tile
  // or a group of tiles for the given set of constraints
  //
  // Constraints included: Media Keys
  //                       Tiling Layouts
  function addGroupOrTileForMediaKeys(
    newTile: Omit<NonEmptyTile, 'mediaKey'>
  ): Tile | Group<Tile> {
    if (mediaKeys == null || mediaKeys.length === 0) {
      throw new Error(`empty mediaKeys`);
    }
    // Create a single tile or a group of tiles depending
    // the number of media keys set

    if (mediaKeys.length === 1) {
      const tile = {
        mediaKey: mediaKeys[0],
        ...newTile,
      };
      tiles.push(tile);
      return tile;
    }

    for (const mediaKey of mediaKeys) {
      const tile = {
        mediaKey,
        ...newTile,
      };
      tiles.push({groupType: 'mediaKeys', items: [tile]});
    }
    return tiles[tiles.length - 1];
  }

  // It returns a range of indices to be used as a lens on
  // the parent collection
  function getAxisData(
    axis: Axis,
    // Min and max indexes to zoom on
    opts?: {
      maxSize?: number;
      min?: number;
      max?: number;
    }
  ): number[] {
    const maxSize = opts?.maxSize;

    if (axis === 'step') {
      const min = opts?.min ?? -Infinity;
      const max = opts?.max ?? Infinity;
      const stepsInRange = (stepMeta.sliderSteps ?? stepMeta.steps).filter(
        s => s >= min && s <= max
      );
      const samples = maxSize
        ? Math.min(stepsInRange.length, maxSize)
        : stepsInRange.length;
      const stepData = maxSize
        ? sampleSorted(stepsInRange, samples)
        : stepsInRange;

      historySampleRate =
        (stepsInRange[stepsInRange.length - 1] - stepsInRange[0] - 2) / samples;

      return stepData;
    }

    if (axis === 'run') {
      // For runs don't sample, take first n
      return _.range(0, runsWithKey.slice(0, maxSize).length);
    }

    if (axis === 'index') {
      // Get the length of the media array
      const metadata =
        runsWithKey[0].history &&
        getMediaMetadata(
          runsWithKey[0].history,
          mediaKeys?.[0] ?? '',
          currentStep
        );

      const mediaGrouping = (metadata && metadata.grouping) || 1;
      const mediaCount = ((metadata && metadata.count) || 1) / mediaGrouping;
      const mediaIndices = _.range(0, mediaCount);

      const res =
        maxSize && mediaCount > maxSize
          ? _.take(mediaIndices, maxSize)
          : mediaIndices;

      return res;
    }

    return _.range(0, 20);
  }
}
